import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PropositoAdjunto, subirAdjuntoDirecto } from "../api";

type AvatarProps = {
  nombre: string;
  especie?: string;
  fotoPerfilUrl?: string | null;
  tamano?: "sm" | "md" | "lg" | "xl";
  editable?: boolean;
  mascotaID?: string;
};

function obtenerEmojiEspecie(especie?: string): string {
  if (!especie) return "🐾";
  const esp = especie.toLowerCase();
  if (esp.includes("perro") || esp.includes("can")) return "🐶";
  if (esp.includes("gato") || esp.includes("felin")) return "🐱";
  if (esp.includes("ave") || esp.includes("pájaro") || esp.includes("loro")) return "🦜";
  if (esp.includes("conejo")) return "🐰";
  if (esp.includes("hamster") || esp.includes("roedor")) return "🐹";
  if (esp.includes("tortuga") || esp.includes("reptil")) return "🐢";
  return "🐾";
}

export default function AvatarMascota({
  nombre,
  especie,
  fotoPerfilUrl,
  tamano = "md",
  editable = false,
  mascotaID,
}: AvatarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emoji = obtenerEmojiEspecie(especie);

  const manejarCambioArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (!archivo || !mascotaID) return;

    if (!archivo.type.startsWith("image/")) {
      setError("Por favor selecciona una imagen válida (JPG, PNG, WEBP).");
      return;
    }

    if (archivo.size > 15 * 1024 * 1024) {
      setError("La imagen no puede superar los 15 MB.");
      return;
    }

    try {
      setSubiendo(true);
      setError(null);
      await subirAdjuntoDirecto(archivo, {
        mascotaID,
        proposito: PropositoAdjunto.FotoPerfil,
      });

      qc.invalidateQueries({ queryKey: ["mascota", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      qc.invalidateQueries({ queryKey: ["adjuntos", mascotaID] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al subir la foto");
    } finally {
      setSubiendo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`avatar-contenedor avatar-${tamano}`}>
      <div className={`avatar-marco ${fotoPerfilUrl ? "con-foto" : "sin-foto"}`}>
        {fotoPerfilUrl ? (
          <img
            src={fotoPerfilUrl}
            alt={`Foto de ${nombre}`}
            className="avatar-img"
            loading="lazy"
          />
        ) : (
          <div className="avatar-placeholder" aria-label={`Avatar de ${nombre}`}>
            <span className="avatar-emoji">{emoji}</span>
          </div>
        )}

        {subiendo && (
          <div className="avatar-overlay-cargando">
            <span className="spinner-chico" />
          </div>
        )}
      </div>

      {editable && mascotaID && (
        <>
          <button
            type="button"
            className="avatar-boton-editar"
            title="Cambiar foto de perfil"
            disabled={subiendo}
            onClick={() => fileInputRef.current?.click()}
          >
            📷
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            style={{ display: "none" }}
            onChange={manejarCambioArchivo}
          />
        </>
      )}

      {error && <p className="avatar-error-tooltip err">{error}</p>}
    </div>
  );
}
